/**
 * 패키지 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layout/AdminLayout';
import DataTable, { Column } from '../../../components/common/DataTable';
import Pagination from '../../../components/common/Pagination';
import FilterBar, { FilterField } from '../../../components/common/FilterBar';
import { productService } from '../../../services/productService';
import { Package, PackageFilter } from '../../../types/product';

const PackagesList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<PackageFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['packages', filter],
    queryFn: () => productService.getPackages(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<Package>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'package_name',
      label: '패키지명',
      render: (pkg) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/products/packages/${pkg.id}`)}
        >
          {pkg.package_name}
        </span>
      ),
    },
    {
      key: 'package_code',
      label: '패키지 코드',
      render: (pkg) => (
        <span style={{ color: '#ccc' }}>{pkg.package_code}</span>
      ),
    },
    {
      key: 'hall_ids',
      label: '포함 관',
      render: (pkg) => (
        <span style={{ color: '#ccc' }}>
          {pkg.hall_ids.length}개 관
        </span>
      ),
    },
    {
      key: 'display_order',
      label: '표시 순서',
      render: (pkg) => (
        <span style={{ color: '#ccc' }}>{pkg.display_order}</span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (pkg) => getStatusBadge(pkg.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '패키지명으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (pkg: Package) => {
    navigate(`/admin/products/packages/${pkg.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>패키지 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/products/packages/new')}>
            패키지 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.packages || []}
              loading={isLoading}
              emptyMessage="패키지가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PackagesList;

